import { User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase.ts';
import { User } from '../models/User';

export const getDisplayName = (user: FirebaseUser | null): string => {
  if (!user) return 'Unknown User';
  return user.displayName || user.email || 'Unknown User';
};

export const syncUserData = async (firebaseUser: FirebaseUser): Promise<User> => {
  console.log('🔄 syncUserData called for:', firebaseUser.email, '(this should NOT log login activity)');
  if (!firebaseUser || !firebaseUser.uid) {
    throw new Error('Invalid user data');
  }

  try {
    const usersRef = collection(db, 'users');
    
    // First, check if we have a user with this Firebase Auth ID
    const firebaseUidQuery = query(usersRef, where('firebaseUid', '==', firebaseUser.uid));
    const firebaseUidSnapshot = await getDocs(firebaseUidQuery);
    
    if (!firebaseUidSnapshot.empty) {
      const existingUserDoc = firebaseUidSnapshot.docs[0];
      if (/^\d+$/.test(existingUserDoc.id)) {
        const existingUser = existingUserDoc.data() as User;
        
        // Update lastLogin and hasCreatedLogin status (but don't log activity - this is session restoration)
        await setDoc(doc(db, 'users', existingUserDoc.id), {
          lastLogin: new Date(),
          hasCreatedLogin: true
        }, { merge: true });

        return {
          ...existingUser,
          id: existingUserDoc.id,
          lastLogin: new Date(),
          hasCreatedLogin: true
        };
      }
    }

    // If not found by UID, check by email to link the account
    if (firebaseUser.email) {
      const emailQuery = query(usersRef, where('email', '==', firebaseUser.email));
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        const existingUserDoc = emailSnapshot.docs[0];
        if (/^\d+$/.test(existingUserDoc.id)) {
          const existingUser = existingUserDoc.data() as User;
          
          // Update the user with Firebase Auth ID, lastLogin, and hasCreatedLogin status (but don't log activity - this is session restoration)
          await setDoc(doc(db, 'users', existingUserDoc.id), {
            firebaseUid: firebaseUser.uid,
            lastLogin: new Date(),
            hasCreatedLogin: true
          }, { merge: true });

          return {
            ...existingUser,
            id: existingUserDoc.id,
            firebaseUid: firebaseUser.uid,
            lastLogin: new Date(),
            hasCreatedLogin: true
          };
        }
      }
    }

    // Fallback for older documents that might use UID as doc ID (should be migrated)
    try {
      const firebaseUidDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (firebaseUidDoc.exists()) {
        console.warn('Found user document with Firebase Auth ID as document ID. This should be migrated.');
        const userData = firebaseUidDoc.data() as User;
        return {
          ...userData,
          id: firebaseUidDoc.id
        };
      }
    } catch (error) {
      // Ignore errors here, proceed to final error
    }

    // If no existing user is found, throw an error
    throw new Error(`No user found for Firebase Auth ID: ${firebaseUser.uid}. Please contact an administrator to create your account.`);
    
  } catch (error) {
    // Don't log login activity here - this is session restoration, not actual login
    // Re-throw the error
    throw error;
  }
}; 